import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';

const articleFAQs = [
  {
    question: 'Can beginners use the Superbuy Spreadsheet?',
    answer:
      'Absolutely. The Superbuy Spreadsheet is designed with beginners in mind. Categories are clearly labeled, product links are organized in an intuitive structure, and QC photos are included where available. Even if you have never shopped from Chinese marketplaces before, you can start finding products within minutes of opening the spreadsheet. The learning curve is minimal, and the community is always ready to help newcomers navigate the process.',
  },
  {
    question: 'How do I verify product quality before buying?',
    answer:
      'Quality verification is a crucial step, and the Superbuy Spreadsheet helps with this in several ways. Look for products that include QC (Quality Control) photos — these are real photographs of the actual product taken by warehouse staff. Check community feedback notes in the spreadsheet. Compare prices across multiple sellers for the same item. When possible, request additional QC photos through your agent before finalizing the purchase.',
  },
  {
    question: 'What should I do if a spreadsheet link is broken?',
    answer:
      'Broken links can happen as sellers update their listings or products go out of stock. If you encounter a dead link, first check if a similar alternative is listed in the same category row. The spreadsheet is regularly maintained to replace broken links, but you can also search for the product name directly on Taobao or Weidian using reverse image search. Community channels are also great places to ask for updated links to specific items.',
  },
  {
    question: 'Is the spreadsheet free to use?',
    answer:
      'Yes, the Superbuy Spreadsheet is a free community resource. The goal is to make product discovery easier and more organized for everyone. There are no hidden fees or subscription requirements. You simply browse the categories, find items you like, and use your preferred shopping agent to make purchases — the spreadsheet itself costs nothing to access and use.',
  },
];

export default function ArticlePage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  return (
    <>
      <Navbar />
      <main>
        <article>
          <section className="relative w-full min-h-[400px] md:min-h-[500px] flex items-center overflow-hidden">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url('https://readdy.ai/api/search-image?query=Organized%20creative%20workspace%20with%20open%20laptop%20displaying%20colorful%20spreadsheet%20data%20surrounded%20by%20style%20magazine%20fabric%20samples%20and%20a%20ceramic%20coffee%20mug%20on%20light%20wood%20desk%20soft%20natural%20morning%20daylight%20from%20large%20window%20cozy%20productive%20editorial%20photography%20warm%20neutral%20tones&width=1600&height=700&seq=article-hero-v3&orientation=landscape')`,
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background-50/50 via-background-50/30 to-background-50/80"></div>
            <div className="relative z-10 w-full px-4 md:px-6 pt-24 pb-12">
              <div className="max-w-3xl mx-auto text-center">
                <nav className="mb-5 text-xs md:text-sm text-foreground-500">
                  <a href="/" className="hover:text-primary-600 transition-colors">Home</a>
                  <span className="mx-2">›</span>
                  <span className="text-foreground-700">Guides</span>
                  <span className="mx-2">›</span>
                  <span className="text-primary-600 font-medium">Superbuy Spreadsheet Guide</span>
                </nav>
                <span className="inline-block px-3 py-1 rounded-full border border-accent-300/50 bg-accent-100/60 text-accent-700 text-xs font-medium mb-4">
                  Updated 2026
                </span>
                <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-semibold text-foreground-950 leading-tight mb-4">
                  Superbuy Spreadsheet Complete Guide 2026
                </h1>
                <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto leading-relaxed">
                  Everything you need to know about finding, using, and mastering the Superbuy Spreadsheet for smarter shopping.
                </p>
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
            <div className="max-w-3xl mx-auto">
              <div className="prose-custom text-sm md:text-base leading-relaxed text-foreground-700 space-y-7">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
                    What Is the Superbuy Spreadsheet?
                  </h2>
                  <p>
                    The <strong>Superbuy Spreadsheet</strong> is a meticulously organized database
                    that brings together thousands of product links from Chinese online marketplaces
                    such as Taobao and Weidian. Think of it as your personal shopping assistant that
                    has already done the hard work of finding, categorizing, and verifying products —
                    so you do not have to spend countless hours scrolling through endless listings on
                    your own.
                  </p>
                  <p className="mt-3">
                    Unlike generic shopping guides or scattered social media recommendation posts,
                    the Superbuy Spreadsheet offers a structured, searchable format where every item
                    is placed into logical categories. This means whether you are looking for the
                    latest sneaker releases, cozy hoodies for winter, or stylish accessories to
                    complete your outfit, you can find them all in one centralized location.
                  </p>
                  <p className="mt-3">
                    The spreadsheet has become widely popular within the replica and alternative
                    fashion communities because it solves a fundamental problem: the overwhelming
                    nature of navigating Chinese marketplaces without knowing the language, without
                    understanding the search algorithms, and without a reliable way to verify product
                    quality. By leveraging community knowledge and organized data, the Superbuy
                    Spreadsheet turns what used to be a frustrating experience into a smooth,
                    enjoyable shopping journey.
                  </p>
                </div>

                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
                    How the Superbuy Spreadsheet Works
                  </h2>
                  <p>
                    At its core, the Superbuy Spreadsheet functions as a collaborative database. Community
                    members and experienced shoppers contribute product links they have personally tested
                    or researched. Each entry typically includes the product name, a direct link to the
                    marketplace listing, the price range, available sizes, and — most importantly — QC
                    (Quality Control) photographs when available.
                  </p>
                  <p className="mt-3">
                    The spreadsheet is organized into clearly defined categories such as Shoes, Hoodies
                    and Sweaters, T-Shirts, Jackets, Pants and Shorts, Headwear, Sets, Underwear, Jerseys,
                    Accessories, Bags, Belts, Perfumes, and a catch-all Other Stuff section. This
                    categorization system makes it incredibly easy to browse for specific types of items
                    without getting lost in an enormous list of unrelated products.
                  </p>

                  <div className="overflow-x-auto mt-5">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="border-b-2 border-background-300">
                          <th className="text-left py-3 pr-4 text-foreground-800 font-semibold">Feature</th>
                          <th className="text-left py-3 pr-4 text-foreground-800 font-semibold">Description</th>
                          <th className="text-left py-3 text-foreground-800 font-semibold">Benefit</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-background-200">
                          <td className="py-3 pr-4 text-foreground-600 font-medium">Organized Categories</td>
                          <td className="py-3 pr-4 text-foreground-500">14+ clearly labeled sections</td>
                          <td className="py-3 text-accent-600">Easy navigation</td>
                        </tr>
                        <tr className="border-b border-background-200">
                          <td className="py-3 pr-4 text-foreground-600 font-medium">QC Photos</td>
                          <td className="py-3 pr-4 text-foreground-500">Real product inspection images</td>
                          <td className="py-3 text-accent-600">Quality assurance</td>
                        </tr>
                        <tr className="border-b border-background-200">
                          <td className="py-3 pr-4 text-foreground-600 font-medium">Size References</td>
                          <td className="py-3 pr-4 text-foreground-500">Fit guides and measurements</td>
                          <td className="py-3 text-accent-600">Fewer returns</td>
                        </tr>
                        <tr>
                          <td className="py-3 pr-4 text-foreground-600 font-medium">Regular Updates</td>
                          <td className="py-3 pr-4 text-foreground-500">New finds added frequently</td>
                          <td className="py-3 text-accent-600">Always fresh content</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
                    Key Benefits of Using the Spreadsheet
                  </h2>
                  <p>
                    The primary advantage of relying on the Superbuy Spreadsheet rather than going
                    solo on Taobao or Weidian is the sheer amount of time you save. Manual searching
                    on these platforms can take hours — you need to figure out the right Chinese
                    search terms, sift through thousands of listings with varying quality, and hope
                    that what you see in the product photos actually matches what arrives at your
                    door.
                  </p>
                  <p className="mt-3">
                    With the spreadsheet, all of this legwork has already been completed for you. The
                    community has filtered out low-quality sellers, identified items with accurate
                    product photos, and organized everything into an easy-to-navigate format. This
                    means you can go from browsing to buying in a fraction of the time it would take
                    otherwise.
                  </p>
                  <p className="mt-3">
                    Another significant benefit is the QC photo integration. When a community member
                    purchases an item through an agent, the agent typically provides detailed
                    photographs of the actual product before shipping. These images are then shared
                    in the spreadsheet, giving future buyers a realistic expectation of what they
                    will receive — something that is nearly impossible to get when browsing
                    marketplaces directly.
                  </p>
                </div>

                <div className="bg-background-950 rounded-2xl p-6 md:p-8 text-center my-8">
                  <h3 className="font-heading text-xl md:text-2xl font-semibold text-background-50 mb-3">
                    Get Access to the Latest Superbuy Spreadsheet
                  </h3>
                  <p className="text-xs md:text-sm text-background-300 mb-5 max-w-md mx-auto">
                    Start discovering products faster with our regularly updated spreadsheet database.
                  </p>
                  <a
                    href={mainSiteUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    Buy It Now
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-up-line text-sm"></i>
                    </div>
                  </a>
                </div>

                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
                    Step-by-Step: How to Use the Superbuy Spreadsheet
                  </h2>
                  <div className="space-y-4">
                    <div className="flex gap-3">
                      <div className="w-7 h-7 shrink-0 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold mt-0.5">1</div>
                      <div>
                        <strong className="text-foreground-900">Open the Spreadsheet</strong>
                        <p className="text-foreground-600 text-sm mt-1">
                          Navigate to the Superbuy Spreadsheet main page. Take a moment to familiarize
                          yourself with the category tabs at the top or side of the document. The layout
                          is designed to be intuitive, so you should be able to find what you need quickly.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-7 h-7 shrink-0 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold mt-0.5">2</div>
                      <div>
                        <strong className="text-foreground-900">Browse Your Preferred Category</strong>
                        <p className="text-foreground-600 text-sm mt-1">
                          Click on a category that interests you — whether it is Shoes, Hoodies,
                          Accessories, or any other section. Each category contains rows of products
                          with links, prices, and available information.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-7 h-7 shrink-0 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold mt-0.5">3</div>
                      <div>
                        <strong className="text-foreground-900">Review Product Details and QC Photos</strong>
                        <p className="text-foreground-600 text-sm mt-1">
                          Before clicking any purchase links, check if QC photos are available. These
                          real-life images of the product help you gauge the actual quality and
                          appearance. Also note any sizing advice or fit comments from the community.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-7 h-7 shrink-0 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold mt-0.5">4</div>
                      <div>
                        <strong className="text-foreground-900">Copy the Product Link</strong>
                        <p className="text-foreground-600 text-sm mt-1">
                          Once you have found an item you like, copy the marketplace link and paste it
                          into your preferred shopping agent platform. Most agents support direct link
                          pasting from Taobao and Weidian.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-7 h-7 shrink-0 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold mt-0.5">5</div>
                      <div>
                        <strong className="text-foreground-900">Complete Your Purchase</strong>
                        <p className="text-foreground-600 text-sm mt-1">
                          Follow your agent's checkout process. Request additional QC photos if needed
                          before the item ships internationally. Once you are satisfied, confirm the
                          shipment and track your package.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
                    Common Mistakes to Avoid
                  </h2>
                  <p>
                    While the Superbuy Spreadsheet makes shopping significantly easier, there are
                    still some pitfalls that beginners should watch out for. First, do not assume
                    that every link in the spreadsheet represents a perfect product — always check
                    the most recent QC photos and read any notes from other buyers. Products can
                    vary between batches, and what looked great six months ago might not meet the
                    same standard today.
                  </p>
                  <p className="mt-3">
                    Second, pay attention to sizing. Chinese sizing often runs smaller than US
                    or European sizing, sometimes by one or even two full sizes. The spreadsheet
                    often includes sizing notes, but it is always wise to measure a similar item
                    you already own and compare it against the seller's size chart before placing
                    an order.
                  </p>
                  <p className="mt-3">
                    Finally, be patient with shipping. International shipping from China through
                    an agent typically takes anywhere from one to four weeks depending on the
                    shipping method you choose. Factor this into your timeline, especially if you
                    are ordering items for a specific event or season.
                  </p>
                </div>

                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
                    Frequently Asked Questions
                  </h2>
                  <div className="space-y-3">
                    {articleFAQs.map((faq, index) => (
                      <div
                        key={index}
                        className="rounded-2xl bg-background-100 border border-background-200 overflow-hidden"
                      >
                        <button
                          onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                          className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                        >
                          <span className="text-sm md:text-base font-medium text-foreground-800 pr-4">
                            {faq.question}
                          </span>
                          <div className={`w-5 h-5 shrink-0 flex items-center justify-center transition-transform duration-300 ${openFAQ === index ? 'rotate-180' : ''}`}>
                            <i className="ri-arrow-down-s-line text-foreground-500"></i>
                          </div>
                        </button>
                        <div className={`overflow-hidden transition-all duration-300 ${openFAQ === index ? 'max-h-96 pb-4' : 'max-h-0'}`}>
                          <p className="px-5 text-xs md:text-sm text-foreground-600 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pt-4">
                  <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
                    Final Thoughts
                  </h2>
                  <p>
                    The Superbuy Spreadsheet has transformed the way thousands of shoppers discover
                    and purchase products from Chinese marketplaces. By centralizing product links,
                    organizing them into intuitive categories, and providing community-verified QC
                    photos, it removes the guesswork and frustration that often accompany
                    international online shopping.
                  </p>
                  <p className="mt-3">
                    Whether you are building your first haul or you are a seasoned buyer looking
                    to streamline your workflow, the spreadsheet is an invaluable tool that saves
                    time, reduces risk, and opens up a world of products you might never have found
                    on your own. Bookmark it, explore the categories, and start shopping smarter
                    today.
                  </p>
                </div>

                <div className="pt-6 pb-4 border-t border-background-200">
                  <a
                    href="/"
                    className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors font-medium"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-left-line text-sm"></i>
                    </div>
                    Back to Superbuy Spreadsheet Home
                  </a>
                </div>
              </div>
            </div>
          </section>
        </article>

        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <h3 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 text-center mb-8">
              Continue Reading
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {[
                {
                  title: 'Quality Control Guide',
                  desc: 'Inspect products like a professional',
                  link: '/qc-guide',
                  img: 'https://readdy.ai/api/search-image?query=Close%20up%20of%20fabric%20texture%20with%20color%20swatches%20and%20measuring%20tape%20on%20cutting%20mat%20bright%20diffused%20studio%20lighting%20fashion%20design%20quality%20inspection%20concept%20editorial%20product%20photography%20clean%20modern%20composition%20warm%20neutral%20tones&width=400&height=280&seq=related-shoes-guide-v3&orientation=landscape',
                },
                {
                  title: 'Safety & Trust Guide',
                  desc: 'Shop with confidence and security',
                  link: '/safety-guide',
                  img: 'https://readdy.ai/api/search-image?query=Magnifying%20glass%20hovering%20over%20garment%20label%20with%20fabric%20detail%20visible%20soft%20studio%20lighting%20on%20light%20grey%20surface%20quality%20check%20inspection%20concept%20minimal%20editorial%20photography%20clean%20composition%20warm%20tones&width=400&height=280&seq=related-beginner-guide-v3&orientation=landscape',
                },
                {
                  title: 'Best Finds of 2026',
                  desc: 'Top community-vetted products',
                  link: '/qc-guide',
                  img: 'https://readdy.ai/api/search-image?query=Secure%20padlock%20icon%20placed%20among%20neatly%20packed%20cardboard%20shipping%20box%20with%20fashion%20items%20soft%20natural%20lighting%20on%20warm%20beige%20surface%20concept%20of%20safe%20delivery%20and%20trusted%20shopping%20experience%20minimal%20editorial%20photography%20clean%20composition&width=400&height=280&seq=related-shipping-guide-v3&orientation=landscape',
                },
              ].map((item) => (
                <a
                  key={item.title}
                  href={item.link || '/superbuy-spreadsheet-guide'}
                  className="group relative aspect-[4/3] rounded-2xl overflow-hidden cursor-pointer"
                >
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                    style={{ backgroundImage: `url('${item.img}')` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/65 via-foreground-950/20 to-transparent"></div>
                  <div className="absolute inset-0 flex flex-col justify-end p-5">
                    <h4 className="text-background-50 font-heading text-lg font-semibold leading-snug mb-1">
                      {item.title}
                    </h4>
                    <p className="text-background-50/75 text-xs">{item.desc}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-foreground-950 mb-4">
              Get Access To Latest Finds
            </h2>
            <p className="text-sm md:text-base text-foreground-600 mb-8 max-w-md mx-auto">
              Start exploring the Superbuy Spreadsheet today and discover thousands of curated products.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-foreground-300 text-foreground-700 text-sm font-medium hover:border-foreground-500 transition-colors whitespace-nowrap w-full sm:w-auto justify-center"
              >
                Superbuy Sheet
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </div>
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap w-full sm:w-auto justify-center"
              >
                Buy It Now
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </div>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'Superbuy Spreadsheet Complete Guide 2026',
            description:
              'Everything you need to know about finding, using, and mastering the Superbuy Spreadsheet for smarter shopping.',
            datePublished: '2026-01-15',
            dateModified: '2026-07-13',
            author: {
              '@type': 'Organization',
              name: 'Superbuy Spread',
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: import.meta.env.VITE_SITE_URL || 'https://superbuyspread.autos',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Guides',
                item: (import.meta.env.VITE_SITE_URL || 'https://superbuyspread.autos') + '/',
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: 'Superbuy Spreadsheet Guide',
                item: (import.meta.env.VITE_SITE_URL || 'https://superbuyspread.autos') + '/superbuy-spreadsheet-guide',
              },
            ],
          }),
        }}
      />
    </>
  );
}